/*eslint-disable*/
import React, { useMemo, useState, useEffect } from 'react'
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Table,
    TableContainer,
    TablePagination,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Typography,
    CircularProgress,
    TableSortLabel,
    Button
} from '@mui/material'
import dayjs from "dayjs";
import { styled } from '@mui/material/styles';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import ActivityInfo from './activityInfo';
import useSort from "../../../../hooks/useSort";
import { selectUserActivitiesArray, selectUserActivitiesStatus } from "../../../../store/selectors/userActivities";
import styles from './activitiesTable.module.scss'
import { getTransactionDetail, sortUserActivities } from './activitiesTable.helpers';
import { useTranslation } from 'react-i18next';
import { ArrowDropDownIcon } from '@mui/x-date-pickers';

const HtmlTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: '#f5f5f9',
        color: 'rgba(0, 0, 0, 0.87)',
        maxWidth: 650,
        width: 580,
        height: 130,
        fontSize: theme.typography.pxToRem(12),
        border: '1px solid #dadde9',
    },
}));

const ActivitiesTable = ({onJsonView}) => {
    const userActivities = useSelector(selectUserActivitiesArray) || [];
    const userActivitiesStatus = useSelector(selectUserActivitiesStatus);
    const areUserActivitiesLoading = userActivitiesStatus === 'loading';
    const { t } = useTranslation()
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [activityInfo, setActivityInfo] = useState({})
    const [showActivityInfoDialog, setShowActivityInfoDialog] = useState(false)
    
    const isLoadingText = t('loading');
    const totalText = t('total');
    
    const resultLabel = `${userActivities.length} ${totalText}`
    const statusLabel = areUserActivitiesLoading ? isLoadingText : resultLabel;

    const { sortBy, sortOrder, handleSort } = useSort({ initialSortBy: 'timeStamp', initialSortOrder: 'asc', setPage })

    const handleChangePage = (event, newPage) => {
        setPage(newPage)
    }

    useEffect(() => {
        setPage(0)
    }, [userActivities])

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value)
        setPage(0)
    }
    const sortedUserActivities = useMemo(() => sortUserActivities({ userActivities, sortBy, sortOrder }), [userActivities, sortBy, sortOrder])

    const renderTableHead = () => (
        <TableRow
            sx={{
                '& th': {
                    backgroundColor: '#F9F8F7',
                    padding: '0 10px',
                },
            }}
        >
            <TableCell className={styles.tableHeader}sx={{width: '50px'}}>
                <div
                    className={styles.userTableHeaderLabel}
                >
                    #
                </div>
            </TableCell>
            <TableCell className={styles.tableHeader} sx={{width: '240px'}}>
                <TableSortLabel
                    className={styles.userTableHeaderLabel}
                    active={sortBy === "timeStamp"}
                    direction={sortOrder}
                    onClick={() => handleSort("timeStamp")}
                >
                    {t('user-activities-table-date')}
                </TableSortLabel>
            </TableCell>
            <TableCell className={styles.userTableHeaderDetails}sx={{width: '137px'}}>
                {t('user-activities-table-method')}
            </TableCell>
            <TableCell className={styles.userTableHeader} sx={{width: '215px'}}>
                <TableSortLabel
                    className={styles.userTableHeaderLabel}
                    active={sortBy === "method"}
                    direction={sortOrder}
                    onClick={() => handleSort("method")}
                >
                    {t('user-activities-table-activity-summary')}
                </TableSortLabel>
            </TableCell>
            <TableCell className={styles.userTableHeader} sx={{width: '240px'}}>
                <TableSortLabel
                    className={styles.userTableHeaderLabel}
                >
                    {t('user-activities-table-additional-details')}
                </TableSortLabel>
            </TableCell>

        </TableRow>
    )

    const closeActivityInfoDialog = () => {
        setShowActivityInfoDialog(false)
        setActivityInfo({})
    }

    const openActivityInfoDialog = (selectedActivityInfo) => {
        setActivityInfo(selectedActivityInfo)
        setShowActivityInfoDialog(true)
    }

    const renderTableBody = () => {
        return sortedUserActivities
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((activity, index) => (
                <TableRow
                    className={styles.tableBodyRow}
                    key={activity.id}
                    onClick={() => openActivityInfoDialog(activity)}
                >
                    <TableCell sx={{ fontSize: '16px' }} className={styles.tableCell}>
                        {index + 1}
                    </TableCell>
                    <TableCell sx={{ fontSize: '16px' }} className={styles.tableCell}>
                        {dayjs(activity.timeStamp).format('MM/DD/YYYY hh:mm:ssA')}
                    </TableCell>
                    <TableCell sx={{ fontSize: '16px' }} className={styles.tableCell}>
                        {activity.method}
                    </TableCell>
                    <TableCell sx={{ fontSize: '16px' }} className={styles.tableCell}>
                        {getTransactionDetail(activity)}
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                    <Button
                        onClick={() => { onJsonView(activity) }}
                        variant="contained"
                        className="primary-btn"
                        disableElevation
                        sx={{
                            color: '#000000',
                            width: '147px',
                            height: '40px',
                            fontWeight: 700,
                            backgroundColor: 'rgba(98, 81, 55, 0.1)',
                            '&:hover': {
                                backgroundColor: 'rgba(102,90,71, 0.20)',
                            },
                            '&:disabled': {
                                backgroundColor: 'rgba(138, 115, 92, 0.05)',
                            },
                            '&:focus': {
                                backgroundColor: 'rgba(112, 99, 79, 0.1)',
                            },
                            }}
                        >
                        <Box
                            sx={{
                            position: 'relative',
                            border: '2px solid black !important',
                            width: '13px !important',
                            height: '13px !important',
                            p: '0 !important',
                            marginRight: '10px'
            
                            }}
                        >
                            <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '2px',
                                borderBottom: '2px solid black !important',
                                p: '0 !important',
                            }}
                            />
                            <ArrowDropDownIcon
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -40%)',
                                fontSize: 17
                            }}
                            />
                        </Box>
                        {t('label-open-json-file')}
                    </Button>

                    </TableCell>
                </TableRow>
            ))
    }
    return (
        <div className={styles.container}>
            <Box sx={{ mb: 1 }}>
                <Typography
                    sx={{
                        color: '#63666A',
                        fontFamily: 'Johnson Text, Arial, sans-serif',
                        fontWeight: 400,
                        fontStyle: 'normal',
                        fontSize: '12px',
                        lineHeight: '100%',
                        letterSpacing: 0,
                        verticalAlign: 'middle',
                        display: 'inline-flex',
                        alignItems: 'center',
                    }}
                >
                    {statusLabel}
                </Typography>
            </Box>
            <div className={styles.formContainer}>
                <TableContainer className={styles.tableContainer}>
                    <Table stickyHeader className={styles.userTable}>
                        <TableHead>{renderTableHead()}</TableHead>
                        <TableBody>
                            {areUserActivitiesLoading ? (
                                <TableRow>
                                    <TableCell sx={{ border: 'none' }} colSpan={6}>
                                        <div className={styles.loaderContainer}>
                                            <div className={styles.loading}>{t('loading')}</div>
                                            <CircularProgress
                                                sx={{
                                                    color: 'black',
                                                }}
                                            />
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                renderTableBody()
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                {userActivities.length > 10 && !areUserActivitiesLoading && (
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 100]}
                        component="div"
                        count={userActivities.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                )}
            </div>
        </div>
    )
}
export default ActivitiesTable